<template>
  <div>
    <page-header
      :title="supplierName"
      :subtitle="$t('supplier_offer_subtitle')"
      icon="mdi-store-outline"
      icon-color="primary"
    >
      <template #actions v-if="!device.isMobile">
        <v-badge
          :content="String(cartCount)"
          :value="cartCount > 0"
          color="error"
          overlap
        >
          <v-btn outlined color="primary" @click="cartDrawer = true">
            <v-icon left>mdi-cart-outline</v-icon>
            {{ $t("cart") }}
            <span v-if="cartCount > 0" class="ml-1"
              >({{ cartTotal }} {{ restaurantCurrency }})</span
            >
          </v-btn>
        </v-badge>
      </template>
    </page-header>

    <!-- Search + grocery filters -->
    <div class="px-6 pt-4">
      <v-row align="center">
        <v-col cols="12" md="5">
          <text-field
            v-model="search"
            kind="search"
            :placeholder="$t('search')"
            hide-details
          />
        </v-col>
      </v-row>

      <div class="mt-3 mb-1" v-if="groceryFilters.length">
        <v-chip
          class="mr-2 mb-2"
          :color="activeGroceryId === null ? 'primary' : 'default'"
          :dark="activeGroceryId === null"
          @click="activeGroceryId = null"
        >
          {{ $t("all") }}
        </v-chip>
        <v-chip
          v-for="g in groceryFilters"
          :key="g.id"
          class="mr-2 mb-2"
          :color="activeGroceryId === g.id ? 'primary' : 'default'"
          :dark="activeGroceryId === g.id"
          @click="activeGroceryId = g.id"
        >
          {{ g.name }}
        </v-chip>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="d-flex justify-center py-12">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <!-- Empty state -->
    <div v-else-if="filteredItems.length === 0" class="text-center py-16 px-6">
      <v-icon size="72" color="grey lighten-1">mdi-package-variant-remove</v-icon>
      <p class="grey--text text--darken-1 mt-4 text-h6">{{ $t("no_results") }}</p>
    </div>

    <!-- Product grid -->
    <div v-else class="px-6 mt-2 pb-16">
      <v-row>
        <v-col
          v-for="item in filteredItems"
          :key="item.id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <v-card outlined class="d-flex flex-column" style="height: 100%">
            <!-- Image / placeholder -->
            <v-img
              :src="item.image_url || ''"
              height="160"
              class="grey lighten-4 flex-grow-0"
            >
              <template v-if="!item.image_url" #default>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-icon size="64" color="grey lighten-1">mdi-food-apple-outline</v-icon>
                </v-row>
              </template>
            </v-img>

            <v-card-text class="pb-1 flex-grow-1">
              <v-chip x-small color="primary" outlined class="mb-2">
                {{ item.grocery.name }}
              </v-chip>
              <div class="font-weight-bold text-body-1 text--primary">
                {{ item.name }}
              </div>
              <div class="text-caption grey--text">{{ $t("code") }}: {{ item.code }}</div>

              <div class="mt-2 d-flex align-end justify-space-between">
                <div>
                  <span class="text-h6 font-weight-bold primary--text">
                    {{ item.price }} {{ restaurantCurrency }}
                  </span>
                  <span class="text-caption grey--text ml-1">/ {{ item.unit }}</span>
                </div>
                <div class="text-caption grey--text text-right">
                  PDV {{ item.pdv }}%<br />
                  <span class="font-weight-medium"
                    >{{ item.price_with_vat }} {{ restaurantCurrency }}</span
                  >
                </div>
              </div>
            </v-card-text>

            <v-card-actions class="px-4 pb-4 pt-0">
              <v-row no-gutters align="center">
                <v-col cols="5">
                  <text-field
                    :value="quantities[item.id] || 1"
                    @input="setQuantity(item.id, $event)"
                    type="number"
                    min="1"
                    step="1"
                    hide-details
                    dense
                    color="primary"
                  />
                </v-col>
                <v-col cols="7" class="pl-2">
                  <v-btn color="primary" block small depressed @click="addToCart(item)">
                    <v-icon small left>mdi-cart-plus</v-icon>
                    {{ $t("add_to_cart") }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Cart navigation drawer -->
    <v-navigation-drawer
      v-model="cartDrawer"
      right
      fixed
      temporary
      width="440"
      style="z-index: 300"
    >
      <v-toolbar color="primary" dark flat>
        <v-icon left>mdi-cart-outline</v-icon>
        <v-toolbar-title>{{ $t("cart") }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon :disabled="cart.length === 0" @click="printCart" :title="$t('print')">
          <v-icon>mdi-printer</v-icon>
        </v-btn>
        <v-btn icon @click="cartDrawer = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <div v-if="cart.length === 0" class="text-center py-16">
        <v-icon size="64" color="grey lighten-1">mdi-cart-off</v-icon>
        <p class="grey--text mt-4">{{ $t("cart_empty") }}</p>
      </div>

      <div v-else class="pa-4">
        <v-list dense>
          <v-list-item v-for="(entry, idx) in cart" :key="idx" class="px-0 mb-2">
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium text-wrap">{{
                entry.item.name
              }}</v-list-item-title>
              <v-list-item-subtitle class="text-caption">
                {{ entry.item.grocery.name }} &bull; {{ entry.item.code }}
              </v-list-item-subtitle>
              <v-list-item-subtitle>
                {{ entry.qty }} × {{ entry.item.price }} =
                <strong
                  >{{ (entry.qty * entry.item.price).toFixed(2) }}
                  {{ restaurantCurrency }}</strong
                >
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon small @click="removeFromCart(idx)">
                <v-icon small color="error">mdi-delete</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <v-divider class="my-3" />

        <div class="d-flex justify-space-between text-body-1 font-weight-bold mb-4">
          <span>{{ $t("cart_total") }}</span>
          <span class="primary--text">{{ cartTotal }} {{ restaurantCurrency }}</span>
        </div>

        <!-- Warehouse selector -->
        <choose-warehouse-field v-model="selectedWarehouseId" hide-details class="mb-3" />

        <v-btn
          color="primary"
          block
          large
          depressed
          :loading="submitting"
          :disabled="!selectedWarehouseId || cart.length === 0"
          @click="submitOrder"
        >
          <v-icon left>mdi-cart-check</v-icon>
          {{ $t("submit_order") }}
        </v-btn>
      </div>
    </v-navigation-drawer>

    <!-- Mobile FAB for cart -->
    <v-btn
      v-if="device.isMobile && cartCount > 0"
      color="primary"
      fab
      fixed
      bottom
      right
      @click="cartDrawer = true"
      style="bottom: 80px"
    >
      <v-badge :content="String(cartCount)" color="error" overlap>
        <v-icon>mdi-cart</v-icon>
      </v-badge>
    </v-btn>
  </div>
</template>

<script>
import ChooseWarehouseField from "@/components/warehouse/fields/ChooseWarehouseField.vue";
import warehouseService from "@/services/warehouse";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrintingV2 from "@/mixins/hasPrintingV2";
import hasWarehousePrinting from "@/mixins/warehouse/hasWarehousePrinting";

export default {
  name: "SupplierOffer",
  components: { ChooseWarehouseField },
  mixins: [hasSnackbar, hasPrintingV2, hasWarehousePrinting],

  data() {
    return {
      loading: false,
      submitting: false,
      items: [],
      selectedWarehouseId: null,
      search: "",
      activeGroceryId: null,
      quantities: {},
      cart: [],
      cartDrawer: false,
    };
  },

  computed: {
    supplierId() {
      return Number(this.$route.params.id);
    },
    supplierName() {
      const found = this.$store.state.supplier.suppliers.find(
        (s) => s.id === this.supplierId
      );
      return found?.name || this.$t("supplier");
    },
    restaurantCurrency() {
      return this.$store.state.restaurant?.settings?.price || "KM";
    },
    groceryFilters() {
      const seen = new Set();
      return this.items
        .filter((i) => i.active)
        .reduce((acc, i) => {
          if (!seen.has(i.grocery.id)) {
            seen.add(i.grocery.id);
            acc.push({ id: i.grocery.id, name: i.grocery.name });
          }
          return acc;
        }, [])
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    filteredItems() {
      return this.items.filter((item) => {
        if (!item.active) return false;
        if (this.activeGroceryId && item.grocery.id !== this.activeGroceryId)
          return false;
        if (this.search) {
          const q = this.search.toLowerCase();
          if (
            !item.name?.toLowerCase().includes(q) &&
            !item.code?.toLowerCase().includes(q) &&
            !item.grocery.name?.toLowerCase().includes(q)
          )
            return false;
        }
        return true;
      });
    },
    cartCount() {
      return this.cart.reduce((sum, e) => sum + e.qty, 0);
    },
    cartTotal() {
      return this.cart
        .reduce((sum, e) => sum + e.qty * Number(e.item.price), 0)
        .toFixed(2);
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await warehouseService.getSupplierCodebook(this.supplierId);
        this.items = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      } catch {
        this.showErrorSnackbar(this.$t("error"));
      } finally {
        this.loading = false;
      }
    },

    setQuantity(itemId, val) {
      const qty = Math.max(1, Number(val) || 1);
      this.$set(this.quantities, itemId, qty);
    },

    addToCart(item) {
      const qty = Number(this.quantities[item.id]) || 1;
      const existing = this.cart.find((e) => e.item.id === item.id);
      if (existing) {
        existing.qty += qty;
      } else {
        this.cart.push({ item, qty });
      }
      this.$set(this.quantities, item.id, 1);
      this.showSuccessSnackbar(`${item.name} → ${this.$t("cart")}`);
    },

    removeFromCart(idx) {
      this.cart.splice(idx, 1);
    },

    async submitOrder() {
      if (!this.selectedWarehouseId || !this.cart.length) return;
      this.submitting = true;
      const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0];
      const user = this.$store.state.user.user;
      const restaurant = this.$store.state.restaurant;
      try {
        await warehouseService.addDocumentTransaction({
          document_type: "order",
          supplier_id: this.supplierId,
          warehouse_id: this.selectedWarehouseId,
          document_date: today,
          ordered_by: user?.id || null,
          groceries: this.cart.map((e) => ({
            grocery_id: e.item.grocery.id,
            warehouse_id: this.selectedWarehouseId,
            name: e.item.name,
            code: e.item.code,
            supplier_code: e.item.code,
            supplier_code_book_id: e.item.id,
            measure: e.item.unit,
            amount: e.qty,
            gross_quantity: e.qty,
            unit_price: Number(e.item.price),
            pdv: Number(e.item.pdv) || 0,
            discount: 0,
            transaction_type: "order",
          })),
        });
        this.showSuccessSnackbar(this.$t("order_submitted_success"));
        this.cart = [];
        this.cartDrawer = false;
      } catch {
        this.showErrorSnackbar(this.$t("error"));
      } finally {
        this.submitting = false;
      }
    },

    printCart() {
      const {
        row,
        col,
        field,
        text,
        table,
        line,
        spacer,
        heading,
        divider,
      } = this.printElements;

      const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0];
      const docLabel = this.$t("warehouse.purchase_order");
      const supplier = this.$store.state.supplier.suppliers.find(
        (s) => s.id === this.supplierId
      ) || { name: this.supplierName };
      const restaurant = this.$store.state.restaurant;
      const user = this.$store.state.user.user;
      const orderedByName = [user?.name, user?.lastname].filter(Boolean).join(" ");

      const headers = [
        { text: this.$t("r_b"), value: "num", width: "40px" },
        { text: this.$t("code"), value: "code", width: "80px" },
        { text: this.$t("title"), value: "name" },
        { text: this.$t("grocery"), value: "grocery_name" },
        {
          text: this.$t("unit_of_measurment"),
          value: "unit",
          align: "center",
          width: "60px",
        },
        { text: this.$t("quantity"), value: "qty", align: "right", width: "70px" },
        { text: this.$t("price"), value: "price", align: "right", width: "90px" },
        { text: this.$t("pdv") + " %", value: "pdv", align: "right", width: "60px" },
        { text: this.$t("amount"), value: "total", align: "right", width: "100px" },
      ];

      const items = this.cart.map((e, i) => ({
        num: i + 1,
        code: e.item.code,
        name: e.item.name,
        grocery_name: e.item.grocery.name,
        unit: e.item.unit,
        qty: e.qty,
        price: Number(e.item.price).toFixed(2),
        pdv: e.item.pdv != null ? e.item.pdv + "%" : "-",
        total: (e.qty * Number(e.item.price)).toFixed(2) + " " + this.restaurantCurrency,
      }));

      items.push({
        num: "",
        code: "",
        name: `<strong>${this.$t("total")}</strong>`,
        grocery_name: "",
        unit: "",
        qty: "",
        price: "",
        pdv: "",
        total: `<strong>${this.cartTotal} ${this.restaurantCurrency}</strong>`,
      });

      this.printDoc(
        [
          row("between align-start", [
            this.printCompanyBlock(restaurant, this.$t("warehouse.orderer")),
            this.printCompanyBlock(supplier, this.$t("supplier")),
          ]),
          spacer(20),
          divider(),
          heading(docLabel),
          `<div style="text-align:center;font-size:11px;color:#777;margin-top:2px">${today}</div>`,
          spacer(12),
          table(headers, items),
          spacer(40),
          row("end", [
            col("center", [
              text(this.$t("warehouse.for_orderer")),
              spacer(16),
              line(),
              text(orderedByName),
            ]),
          ]),
        ],
        { pageTitle: docLabel }
      );
    },
  },
};
</script>

<style scoped>
.offer-card {
  transition: box-shadow 0.2s;
}
.offer-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12) !important;
}
</style>
